import { Fragment, useState, useEffect } from "react";
import PropertySearch from "../../components/property-search";
import { Link } from "react-router-dom";
import { PropertyCard } from "../../components/property-card";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Scale,
  X,
  Plus,
  TrendingUp,
  Home,
  Ruler,
  DollarSign,
  Search,
  Map,
  CheckCircle2,
} from "lucide-react";
import {
  formatCurrency,
  formatSquareFeet,
} from "../../utils/property-formatters";
import NearbyPropertiesMap from "../../components/NearbyPropertiesMap";
import { useNearbyProperties } from "../../hooks";
import { getBuildingSquareFeet } from "../../utils/property-calculations";

export const ComparePropertyEnhanced = ({
  property = {},
  propertyId,
  embedded = false,
  onOpenRef,
  marketValuePerSqft = 0,
  setMarketValuePerSqft = () => {},
  onComparableSelect = () => {},
  onSelectedComparablesChange = () => {},
}) => {
  const [properties, setProperties] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [open, setOpen] = useState(false);
  const [viewMode, setViewMode] = useState('map'); // 'search' or 'map' - default to map

  useEffect(() => {
    onSelectedComparablesChange(selectedProperties);
  }, [selectedProperties, onSelectedComparablesChange]);

  // Expose the dialog open function to parent via ref callback
  if (onOpenRef) {
    onOpenRef.current = () => setOpen(true);
  }
  
  // Fetch properties for map view from both APIs (comparables + search)
  const { 
    properties: nearbyProperties, 
    loading: loadingNearby 
  } = useNearbyProperties(propertyId, property);

  const handlePropertySearch = (properties) => {
    setProperties(properties);
  };

  const MAX_COMPARABLES = 3;

  const addComparableProperty = (prop) => {
    setOpen(false);
    setSelectedProperties((prev) => {
      if (prev.length >= MAX_COMPARABLES) return prev;
      if (prev.some((p) => p.id === prop.id)) return prev;
      return [...prev, prop];
    });
    onComparableSelect(prop);
  };

  const handlePropertyToCompareSelect = (prop) => () => {
    addComparableProperty(prop);
  };

  const handlePropertyFromSearch = (prop) => {
    addComparableProperty(prop);
  };

  const handleRemoveProperty = (propertyId) => {
    setSelectedProperties((prev) => prev.filter((p) => p.id !== propertyId));
  };

  // Calculate comparison metrics (excludes current property from averages)
  const getComparisonMetrics = () => {
    const compareProperties = selectedProperties;

    const metrics = {
      avgValue: 0,
      avgSqft: 0,
      avgPricePerSqft: 0,
      maxPricePerSqft: 0,
      highestValue: null,
      lowestValue: null,
    };

    if (compareProperties.length === 0) return metrics;

    let totalValue = 0;
    let totalSqft = 0;
    let totalPricePerSqft = 0;
    let validCount = 0;
    let validSqftCount = 0;
    let maxPpsf = 0;

    compareProperties.forEach((prop) => {
      const value = prop.estimatedValue || 0;
      const sqft = getBuildingSquareFeet(prop) || 0;

      if (value > 0) {
        totalValue += value;
        validCount++;

        if (!metrics.highestValue || value > metrics.highestValue.value) {
          metrics.highestValue = { ...prop, value };
        }
        if (!metrics.lowestValue || value < metrics.lowestValue.value) {
          metrics.lowestValue = { ...prop, value };
        }
      }

      if (sqft > 0) {
        totalSqft += sqft;
        validSqftCount++;
        if (value > 0) {
          const ppsf = value / sqft;
          totalPricePerSqft += ppsf;
          if (ppsf > maxPpsf) {
            maxPpsf = ppsf;
          }
        }
      }
    });

    metrics.avgValue = validCount > 0 ? Math.ceil(totalValue / validCount) : 0;
    metrics.avgSqft =
      validSqftCount > 0
        ? Math.ceil(totalSqft / validSqftCount)
        : 0;
    metrics.avgPricePerSqft =
      validCount > 0 ? Math.ceil(totalPricePerSqft / validCount) : 0;
    metrics.maxPricePerSqft = Math.ceil(maxPpsf);

    return metrics;
  };

  const metrics = getComparisonMetrics();

  // Auto-update Similar Renovated Properties Value when avg $/sqft from comparisons exceeds it
  useEffect(() => {
    if (
      selectedProperties.length > 0 &&
      metrics.avgPricePerSqft > 0 &&
      metrics.avgPricePerSqft > marketValuePerSqft &&
      metrics.maxPricePerSqft > 0
    ) {
      setMarketValuePerSqft(metrics.maxPricePerSqft);
    }
  }, [selectedProperties, metrics.avgPricePerSqft, metrics.maxPricePerSqft, marketValuePerSqft, setMarketValuePerSqft]);

  const AdditionalDetails = (property) => {
    const { yearBuilt } = property;
    const squareFeet = getBuildingSquareFeet(property);
    return (
      <Fragment>
        <div className="flex justify-between text-xs">
          <span className="text-muted-foreground">Area:</span>
          <span className="font-bold ml-1">
            {squareFeet > 0 ? `${squareFeet.toLocaleString()} sqft` : "N/A"}
          </span>
        </div>
        {yearBuilt && (
          <div className="flex justify-between text-xs mt-1">
            <span className="text-muted-foreground">Built:</span>
            <span className="font-bold ml-1">{yearBuilt}</span>
          </div>
        )}
      </Fragment>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header - hidden in embedded mode */}
      {!embedded && (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Scale className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-foreground">
                Property Comparison
              </h2>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                Compare this property with similar listings
              </p>
            </div>
          </div>

          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={() => setOpen(true)}
              size="default"
              className="gap-2 bg-primary hover:bg-primary/90 text-white shadow-md flex-1 sm:flex-none"
            >
              <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="hidden xs:inline">
                {selectedProperties && selectedProperties.length > 0
                  ? "Add More"
                  : "Add Properties"}
              </span>
              <span className="xs:hidden">Add</span>
            </Button>
            {selectedProperties && selectedProperties.length > 0 && (
              <Button
                variant="outline"
                size="default"
                className="gap-2 text-destructive hover:text-destructive flex-1 sm:flex-none"
                onClick={() => setSelectedProperties([])}
              >
                <X className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="hidden xs:inline">Clear All</span>
                <span className="xs:hidden">Clear</span>
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Embedded mode: Clear All button when properties are selected */}
      {embedded && selectedProperties.length > 0 && (
        <div className="flex justify-end">
          <Button
            variant="outline"
            size="sm"
            className="gap-2 text-destructive hover:text-destructive"
            onClick={() => setSelectedProperties([])}
          >
            <X className="h-4 w-4" />
            Clear All
          </Button>
        </div>
      )}

      {/* Comparison Metrics */}
      {selectedProperties.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="shadow-md">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                    Avg. Value
                  </p>
                  <p className="text-base sm:text-lg md:text-xl font-bold text-primary">
                    {formatCurrency(metrics.avgValue)}
                  </p>
                </div>
                <TrendingUp className="h-6 w-6 sm:h-8 sm:w-8 text-primary/30" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-md">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                    Avg. Size
                  </p>
                  <p className="text-base sm:text-lg md:text-xl font-bold text-blue-600">
                    {formatSquareFeet(metrics.avgSqft)}
                  </p>
                </div>
                <Ruler className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600/30" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-md">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                    Avg. $/sq ft
                  </p>
                  <p className="text-base sm:text-lg md:text-xl font-bold text-green-600">
                    {formatCurrency(metrics.avgPricePerSqft)}
                  </p>
                </div>
                <DollarSign className="h-6 w-6 sm:h-8 sm:w-8 text-green-600/30" />
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-md">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">
                    Comparing
                  </p>
                  <p className="text-base sm:text-lg md:text-xl font-bold text-purple-600">
                    {selectedProperties.length + 1} Properties
                  </p>
                </div>
                <Home className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600/30" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Property Cards Comparison */}
      {selectedProperties && selectedProperties.length > 0 ? (
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
              <Scale className="h-4 w-4 sm:h-5 sm:w-5" />
              Side-by-Side Comparison
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Mobile: Stacked Layout */}
            <div className="grid grid-cols-1 gap-6 md:hidden">
              {/* Current Property */}
              <div className="relative">
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white shadow-lg px-4 py-1.5 whitespace-nowrap z-10 text-xs">
                  Current Property
                </Badge>
                <div className="ring-4 ring-primary/50 rounded-lg mt-2">
                  <PropertyCard
                    additionalDetailsRenderer={AdditionalDetails}
                    property={{
                      ...property,
                      photoUrl: property?.photoUrls?.[0],
                    }}
                    showPrices={true}
                    imageHeight={180}
                    redirectOnClick={false}
                  />
                </div>
              </div>

              {/* Selected Properties */}
              {selectedProperties.map((prop) => (
                <div key={prop.id} className="relative">
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute -top-3 -right-2 z-10 h-8 w-8 rounded-full shadow-lg"
                    onClick={() => handleRemoveProperty(prop.id)}
                  >
                    <X className="h-3 w-3" />
                  </Button>
                  <Link to={`/property/${prop.id}`} target="_blank">
                    <PropertyCard
                      redirectOnClick={false}
                      additionalDetailsRenderer={AdditionalDetails}
                      property={prop}
                      showPrices={true}
                      showImageModalOnClick={false}
                      imageHeight={180}
                    />
                  </Link>
                </div>
              ))}

              {/* Add More Card */}
              <Card
                className="cursor-pointer hover:border-primary hover:shadow-lg transition-all"
                onClick={() => setOpen(true)}
              >
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <div className="h-14 w-14 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                    <Plus className="h-7 w-7 text-primary" />
                  </div>
                  <p className="text-base font-semibold text-center">
                    Add Property
                  </p>
                  <p className="text-xs text-muted-foreground text-center mt-1">
                    Click to compare more
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Tablet & Desktop: Horizontal Scroll */}
            <div className="hidden md:block overflow-x-auto pb-4">
              <div className="flex items-stretch gap-4 lg:gap-6 min-w-max pt-4 px-1">
                {/* Current Property */}
                <div className="flex-shrink-0 w-[240px] lg:w-[280px]">
                  <div className="relative pt-4">
                    <div className="ring-4 ring-primary/50 rounded-lg">
                      <PropertyCard
                        additionalDetailsRenderer={AdditionalDetails}
                        property={{
                          ...property,
                          photoUrl: property?.photoUrls?.[0],
                        }}
                        showPrices={true}
                        width="100%"
                        imageHeight={160}
                        redirectOnClick={false}
                      />
                    </div>
                    <Badge className="absolute -top-1 left-1/2 -translate-x-1/2 bg-primary text-white shadow-lg px-3 py-1 text-xs whitespace-nowrap">
                      Current Property
                    </Badge>
                  </div>
                </div>

                {/* Selected Properties */}
                {selectedProperties.map((prop) => (
                  <div
                    key={prop.id}
                    className="flex-shrink-0 relative pt-4 w-[240px] lg:w-[280px]"
                  >
                    <Button
                      variant="destructive"
                      size="icon"
                      className="absolute top-0 -right-2 z-10 h-8 w-8 rounded-full shadow-lg"
                      onClick={() => handleRemoveProperty(prop.id)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                    <Link to={`/property/${prop.id}`} target="_blank">
                      <div className="hover:scale-105 transition-transform">
                        <PropertyCard
                          redirectOnClick={false}
                          additionalDetailsRenderer={AdditionalDetails}
                          property={prop}
                          showPrices={true}
                          showImageModalOnClick={false}
                          width="100%"
                          imageHeight={160}
                        />
                      </div>
                    </Link>
                  </div>
                ))}

                {/* Add More Card */}
                <div className="flex-shrink-0 pt-4 flex w-[240px] lg:w-[280px]">
                  <Card
                    className="flex-1 cursor-pointer hover:border-primary hover:shadow-lg transition-all flex items-center justify-center min-h-[300px]"
                    onClick={() => setOpen(true)}
                  >
                    <CardContent className="flex flex-col items-center justify-center py-8">
                      <div className="h-12 w-12 lg:h-16 lg:w-16 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                        <Plus className="h-6 w-6 lg:h-8 lg:w-8 text-primary" />
                      </div>
                      <p className="text-base lg:text-lg font-semibold text-center">
                        Add Property
                      </p>
                      <p className="text-xs lg:text-sm text-muted-foreground text-center mt-2">
                        Click to compare more
                      </p>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : embedded ? (
        <div className="py-6 sm:py-8">
          <div className="text-center space-y-3 sm:space-y-4">
            <div className="mx-auto w-16 h-16 sm:w-20 sm:h-20 bg-muted rounded-full flex items-center justify-center">
              <Scale className="w-8 h-8 sm:w-10 sm:h-10 text-muted-foreground" />
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-semibold mb-2">
                Start Comparing Properties
              </h3>
              <p className="text-xs sm:text-sm text-muted-foreground max-w-md mx-auto px-2">
                Add properties to compare features, prices, and specifications
                side-by-side to make an informed decision.
              </p>
            </div>
            <Button
              onClick={() => setOpen(true)}
              size="default"
              className="gap-2 bg-primary hover:bg-primary/90 text-white mt-2 sm:mt-4"
            >
              <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="text-sm sm:text-base">
                Add Properties to Compare
              </span>
            </Button>
          </div>
        </div>
      ) : (
        <Card className="shadow-md">
          <CardContent className="py-8 sm:py-12 px-4">
            <div className="text-center space-y-3 sm:space-y-4">
              <div className="mx-auto w-16 h-16 sm:w-20 sm:h-20 bg-muted rounded-full flex items-center justify-center">
                <Scale className="w-8 h-8 sm:w-10 sm:h-10 text-muted-foreground" />
              </div>
              <div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2">
                  Start Comparing Properties
                </h3>
                <p className="text-xs sm:text-sm text-muted-foreground max-w-md mx-auto px-2">
                  Add properties to compare features, prices, and specifications
                  side-by-side to make an informed decision.
                </p>
              </div>
              <Button
                onClick={() => setOpen(true)}
                size="default"
                className="gap-2 bg-primary hover:bg-primary/90 text-white mt-2 sm:mt-4"
              >
                <Plus className="h-4 w-4 sm:h-5 sm:w-5" />
                <span className="text-sm sm:text-base">
                  Add Properties to Compare
                </span>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-[90vw] w-full flex flex-col p-0 gap-0 h-[90vh] sm:h-[85vh]">
          <DialogHeader className="px-3 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 border-b bg-muted/30">
            <DialogTitle className="text-lg sm:text-2xl font-bold flex items-center gap-2">
              <Scale className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
              <span>Add Properties to Compare</span>
            </DialogTitle>
            <p className="text-xs sm:text-sm text-muted-foreground mt-1 sm:mt-2">
              {viewMode === 'search' 
                ? 'Search and select properties to add to your comparison'
                : 'Click on nearby properties on the map to add them'
              }
            </p>
            
            {/* View Mode Toggle */}
            <div className="flex gap-2 mt-3 sm:mt-4">
              <Button
                variant={viewMode === 'search' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('search')}
                className="gap-2"
              >
                <Search className="h-4 w-4" />
                Search View
              </Button>
              <Button
                variant={viewMode === 'map' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setViewMode('map')}
                className="gap-2"
              >
                <Map className="h-4 w-4" />
                Map View
              </Button>
            </div>
          </DialogHeader>

          {/* Search Bar - Only show in search mode */}
          {viewMode === 'search' && (
            <div className="px-3 sm:px-6 py-3 sm:py-4 border-b bg-background">
              <PropertySearch
                onChange={handlePropertySearch}
                onPropertySelectFromSearch={handlePropertyFromSearch}
                setIsLoadingProperties={setIsLoading}
              />
            </div>
          )}

          <div className={`flex-1 ${viewMode === 'search' ? 'overflow-y-auto' : 'overflow-hidden'} px-3 sm:px-6 py-3 sm:py-4`}>
            {/* Map View */}
            {viewMode === 'map' ? (
              <div className="h-full min-h-[500px]">
                {loadingNearby ? (
                  <div className="flex items-center justify-center h-full min-h-[500px]">
                    <div className="text-center space-y-3">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                      <p className="text-sm text-muted-foreground">Loading nearby properties...</p>
                    </div>
                  </div>
                ) : nearbyProperties.length > 0 ? (
                  <div className="h-full min-h-[500px] flex flex-col gap-3">
                    <div className="flex items-center justify-between flex-shrink-0">
                      <Badge variant="secondary" className="text-xs sm:text-sm">
                        {nearbyProperties.length} nearby {nearbyProperties.length === 1 ? 'property' : 'properties'}
                      </Badge>
                      <p className="text-xs text-muted-foreground">
                        Click on a marker to add property
                      </p>
                    </div>
                    <div className="flex-1 min-h-[450px] rounded-lg overflow-hidden border">
                      <NearbyPropertiesMap
                        properties={nearbyProperties}
                        centerProperty={property}
                        height="100%"
                        customAction={{
                          text: 'Add to Comparison',
                          icon: Plus,
                          onClick: (selectedProp) => {
                            // Check if already selected
                            const isAlreadySelected = selectedProperties.some(
                              (p) => p.id === selectedProp.id
                            );
                            if (!isAlreadySelected) {
                              handlePropertyToCompareSelect(selectedProp)();
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-center h-full">
                    <Card className="max-w-md w-full p-6 sm:p-8 shadow-lg">
                      <div className="text-center space-y-3 sm:space-y-4">
                        <div className="mx-auto w-14 h-14 sm:w-16 sm:h-16 bg-muted rounded-full flex items-center justify-center">
                          <Map className="w-7 h-7 sm:w-8 sm:h-8 text-muted-foreground" />
                        </div>
                        <div>
                          <h3 className="text-base sm:text-lg font-semibold mb-2">
                            No Nearby Properties Found
                          </h3>
                          <p className="text-xs sm:text-sm text-muted-foreground">
                            Try using the search view to find properties to compare
                          </p>
                        </div>
                        <Button
                          onClick={() => setViewMode('search')}
                          variant="outline"
                          className="gap-2"
                        >
                          <Search className="h-4 w-4" />
                          Switch to Search
                        </Button>
                      </div>
                    </Card>
                  </div>
                )}
              </div>
            ) : (
              /* Search View */
              <>
                {isLoading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                {[...Array(8)].map((_, index) => (
                  <Card key={index} className="overflow-hidden">
                    <Skeleton className="h-[140px] sm:h-[180px] w-full" />
                    <div className="p-2 sm:p-3 space-y-2">
                      <Skeleton className="h-3 w-3/4" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                  </Card>
                ))}
              </div>
            ) : properties.length > 0 ? (
              <div>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3 sm:mb-4">
                  <Badge
                    variant="secondary"
                    className="text-xs sm:text-sm w-fit"
                  >
                    {properties.length}{" "}
                    {properties.length === 1 ? "property" : "properties"} found
                  </Badge>
                  <p className="text-xs text-muted-foreground">
                    Click on a property to add it for comparison
                  </p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                  {properties.map((property) => {
                    const isSelected = selectedProperties.some(
                      (p) => p.id === property.id
                    );
                    return (
                      <div
                        key={property.id}
                        className={`cursor-pointer transition-all ${
                          isSelected
                            ? "opacity-50 pointer-events-none"
                            : "hover:scale-105"
                        }`}
                        onClick={
                          isSelected
                            ? undefined
                            : handlePropertyToCompareSelect(property)
                        }
                      >
                        <PropertyCard
                          redirectOnClick={false}
                          property={property}
                        />
                        {isSelected && (
                          <Badge
                            className="mt-2 w-full justify-center text-xs"
                            variant="secondary"
                          >
                            Already Added
                          </Badge>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full min-h-[300px] sm:min-h-[400px]">
                <Card className="max-w-md w-full p-6 sm:p-8 shadow-lg mx-4">
                  <div className="text-center space-y-3 sm:space-y-4">
                    <div className="mx-auto w-14 h-14 sm:w-16 sm:h-16 bg-muted rounded-full flex items-center justify-center">
                      <svg
                        className="w-7 h-7 sm:w-8 sm:h-8 text-muted-foreground"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h3 className="text-base sm:text-lg font-semibold mb-2">
                        Search for Properties
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground">
                        Use the search bar above to find properties to compare
                        with your current selection
                      </p>
                    </div>
                  </div>
                </Card>
              </div>
            )}
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
